/* eslint-disable max-len */
import { get } from 'lodash-es';
import { CHART_TYPES } from '../enums';

export const translateColor = (color, context) => {
  if (!color) {
    return;
  }
  color = color.replace('var(', '').replace(')', ''); // if color appears in "var()", remove the "var()"
  const colorvar = getComputedStyle(context.shadowRoot.host).getPropertyValue(
    color
  );
  if (colorvar && colorvar.length > 0) {
    return colorvar.replace(' ', '');
  }
  return color;
};

export const translateColorArray = (colors, context) => {
  if (!colors) {
    return;
  }
  colors = Array.isArray(colors) ? colors : [colors];
  return colors.map(color => translateColor(color, context));
};

/**
 *
 * @param {string} color color to add opacity to
 * @param {number} opacity opacity to apply, in decimal
 * @return {string|*} color with the applied opacity
 */
export const addOpacity = (color, opacity) => {
  if (color.includes('rgb')) {
    return color.replace(')', `, ${opacity})`);
  } else if (color.includes('#')) {
    return `${color}${opacity * 100}`;
  }
};


export const DEFAULT_COLORS = [
  'var(--fc-turquoise-500)',
  'var(--fc-ocean-500)',
  'var(--fc-brand-blue-500)',
  'var(--fc-indigo-500)',
  'var(--fc-purple-500)',
  'var(--fc-pink-purple-500)',
  'var(--fc-magenta-500)',
  'var(--fc-orange-500)',
  'var(--fc-gold-500)',
  'var(--fc-olive-500)'
];

const TREEMAP_COLORS = {
  FROM: '#90BDFF',
  TO: '#002255'
};

const FUNNEL_COLORS = {
  FROM: '#0358d7',
  TO: '#90BDFF'
};

const getDimensionValueColor = ({ dimensionsStyle, dataSource, dimensionField, normalizedDimensionValue }) => get(dimensionsStyle, [dataSource, dimensionField, normalizedDimensionValue])?.color;

const getPredefinedDimensionValuesColors = ({ dimensionsStyle, dataSource, dimensionFieldNames, data, xAxisType, label }) => {
  // depending on the x axis type, this function extracts dimension value styling that were predefined in the dimensionsStyle object
  if (xAxisType === 'SINGLE_NON_TIME_FIELD') {
    let colors = data.map(({ dimensions }) => {
      const [dimensionField] = dimensionFieldNames;
      const [dimensionValue] = dimensions;
      const normalizedDimensionValue = dimensionValue.toLowerCase();
      if (['other'].includes(normalizedDimensionValue) ) {
        return 'var(--fc-gray-500)';
      }
      return getDimensionValueColor({ dimensionsStyle, dimensionField, normalizedDimensionValue, dataSource });
    });
    colors = colors.filter(Boolean);
    return colors;
  } else if (xAxisType === 'SINGLE_TIME_FIELD_SINGLE_NON_TIME_FIELD') {
    const [, dimensionField] = dimensionFieldNames;
    const normalizedDimensionValue = label.toLowerCase();
    return getDimensionValueColor({ dimensionsStyle, dimensionField, normalizedDimensionValue, dataSource });
  }
};

/**
  * Blend two colors together by percentage
  * @param color1 color to blend
  * @param color2 color to blend
  * @param percent how much in percent should color1 match color2
  **/

const blendColorsByPercent = (color1, color2, percent) => {
  const red1 = parseInt(color1[1] + color1[2], 16);
  const green1 = parseInt(color1[3] + color1[4], 16);
  const blue1 = parseInt(color1[5] + color1[6], 16);

  const red2 = parseInt(color2[1] + color2[2], 16);
  const green2 = parseInt(color2[3] + color2[4], 16);
  const blue2 = parseInt(color2[5] + color2[6], 16);

  const red = Math.round(mix(red1, red2, percent));
  const green = Math.round(mix(green1, green2, percent));
  const blue = Math.round(mix(blue1, blue2, percent));

  return generateHex(red, green, blue);
};

/**
 * Converts RGB to Hex
 * @param {*} r red
 * @param {*} g green
 * @param {*} b blue
 */

const generateHex = (r, g, b) => {
  r = r.toString(16);
  g = g.toString(16);
  b = b.toString(16);

  if (r.length < 2) r = `0${r}`;
  if (g.length < 2) g = `0${g}`;
  if (b.length < 2) b = `0${b}`;

  return `#${r + g + b}`;
};

const mix = (start, end, percent) => {
  return start + ((percent) * (end - start));
};

export const updateColorsInDatasets = (config, chartJSConfig, context) => {
  const { xAxisType, dimensionsStyle, dataSource } = config;
  chartJSConfig.data.datasets = chartJSConfig.data.datasets.map((dataset, index, array) => {
    if (xAxisType === 'SINGLE_TIME_FIELD') {
      // single time field means the datasets are per different kpis, and therefore color overrrides can come from the config
      dataset.backgroundColor = dataset.backgroundColor || DEFAULT_COLORS[index];
      dataset.borderColor = dataset.borderColor || DEFAULT_COLORS[index];
    } else if (xAxisType === 'SINGLE_NON_TIME_FIELD') {
      const { dimensionFieldNames, data } = dataset;
      const predefinedColors = getPredefinedDimensionValuesColors({ dimensionsStyle, dataSource, dimensionFieldNames, data, xAxisType });
      dataset.backgroundColor = dataset.backgroundColor ? dataset.backgroundColor : (predefinedColors?.length > 0 ? predefinedColors : DEFAULT_COLORS);
      dataset.borderColor = dataset.borderColor ? dataset.borderColor : (predefinedColors?.length > 0 ? predefinedColors : DEFAULT_COLORS);
    } else {
      // in this case the datasets are per different dimension values
      const { dimensionFieldNames, label } = dataset;
      const predefinedColor = getPredefinedDimensionValuesColors({ dimensionsStyle, dataSource, dimensionFieldNames, xAxisType, label });
      dataset.backgroundColor = dataset.backgroundColor ? dataset.backgroundColor : (predefinedColor ? predefinedColor : DEFAULT_COLORS[index]);
      dataset.borderColor = dataset.borderColor ? dataset.borderColor : (predefinedColor ? predefinedColor : DEFAULT_COLORS[index]);
    }

    const isTreeMap = dataset.type === CHART_TYPES.TREEMAP;
    const isFunnel = chartJSConfig.type === CHART_TYPES.FUNNEL;

    dataset.backgroundColor = translateColorArray(dataset.backgroundColor, context);

    // Treemap color blending
    if (isTreeMap) {
      dataset.backgroundColor = ctx => blendColorsByPercent(
        TREEMAP_COLORS.FROM,
        TREEMAP_COLORS.TO,
        ctx.index * (1 / dataset.data.length) // Calculates percentage based on index
      );
      if (context.renderRoot?.children[0]) {
        context.renderRoot.children[0].style.filter = 'saturate(1.4)';
      }
    }

    if (isFunnel) {
      dataset.backgroundColor = ctx => blendColorsByPercent(
        FUNNEL_COLORS.FROM,
        FUNNEL_COLORS.TO,
        index * (1 / array.length)
      );
      if (context.renderRoot?.children[0]) {
        context.renderRoot.children[0].style.filter = 'saturate(1.4)';
      }
    }

    dataset.borderColor = translateColorArray(
      dataset.borderColor,
      context);

    return dataset;
  });

  return chartJSConfig;
};
